/**
 * Mi Cuenta · "Compras y facturas" y el modal "Generar factura"
 * (spec docs/superpowers/specs/2026-10-08-micuenta-generar-factura-design.md §4.1 y §4.6).
 *
 * La tarjeta (`noble/cuenta/_compras`, `[data-fd-compras]`) lista las compras pagadas de un producto con el estado
 * que decide el servidor (Orden_factura::estado()): "Generar factura" en las del mes en curso, PDF y XML en las
 * facturadas, y el texto de cada estado. El modal (`noble/cuenta/_factura_modal`, `#fd-fc-modal`) tiene tres pasos:
 *   1 datos     la casilla "Facturar a los datos fiscales de esta cuenta" (marcada) carga RFC, razón social y CP de
 *               la cuenta en solo lectura; el régimen sale de los de la cuenta y el uso se filtra por régimen;
 *   2 confirmar el resumen, con "Corregir" y "Generar factura" (que se desactiva al primer clic);
 *   3 resultado PDF y XML, "estamos confirmando", o de vuelta al paso 1 con el dato que el SAT rechazó. Si la
 *               compra ya tenía un CFDI (un intento anterior que sí timbró), se dice a nombre de quién está.
 * El aviso de que la factura se genera una sola vez y después ya no cambia es fijo en los pasos 1 y 2.
 * Sin respuesta legible de generar_ajax: 403/419/429 los responde el servidor antes de tocar la orden (no se envió
 * nada); cualquier otra cosa NO se reintenta (pudo timbrarse) y se confirma (falloGenerar).
 *
 * Reglas: todo AJAX por window.FD.ajax (inyecta X-FD-Csrf), nada de onclick en línea, y el servidor vuelve a
 * validar TODO (esto es el espejo de Orden_factura::validar_receptor). Las funciones exportadas son puras (Vitest,
 * entorno node); el montaje solo corre en el navegador.
 */
import * as totales from './totales.js';

export var AVISO = 'Revisa con calma los datos: esta factura se genera una sola vez y después ya no cambia.';
export var MSG_GENERICO = 'Para público en general no se emite factura nominativa';
export var MSG_NO_EMITIDA = 'No se emitió ninguna factura; corrige el dato y vuelve a intentar.';

var GENERICOS = ['XAXX010101000', 'XEXX010101000'];
var USOS_EXCLUIDOS = ['CN01', 'CP01'];

function txt(v) { return String(v == null ? '' : v); }

export function normalizarRfc(v) { return txt(v).toUpperCase().replace(/\s+/g, ''); }

export function normalizarNombre(v) { return txt(v).toUpperCase().replace(/\s+/g, ' ').trim(); }

/** moral | fisica | generico | '' (largo en caracteres, como Cfdi_rules::tipo_persona). */
export function tipoPersona(rfc) {
  var r = normalizarRfc(rfc);
  if (GENERICOS.indexOf(r) >= 0) { return 'generico'; }
  var n = Array.from(r).length;
  if (n === 12) { return 'moral'; }
  if (n === 13) { return 'fisica'; }
  return '';
}

export function validarRfc(rfc) {
  var r = normalizarRfc(rfc);
  if (!r) { return { ok: false, msg: 'Escribe el RFC.' }; }
  if (GENERICOS.indexOf(r) >= 0) { return { ok: false, msg: MSG_GENERICO + '.' }; }
  if (!/^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/.test(r)) { return { ok: false, msg: 'El RFC no tiene un formato válido.' }; }
  return { ok: true, rfc: r };
}

export function validarCp(cp) {
  var c = txt(cp).trim();
  return /^\d{5}$/.test(c) ? { ok: true, cp: c } : { ok: false, msg: 'El código postal debe tener 5 dígitos.' };
}

/** Uno o dos correos válidos, sin repetir; null si alguno no vale o son más de dos. */
export function correos(texto) {
  var partes = txt(texto).split(/[,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
  if (!partes.length || partes.length > 2) { return null; }
  var vistos = {};
  var out = [];
  for (var i = 0; i < partes.length; i++) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partes[i])) { return null; }
    var k = partes[i].toLowerCase();
    if (!vistos[k]) { vistos[k] = true; out.push(partes[i]); }
  }
  return out;
}

/** Regímenes del catálogo que aplican al tipo de persona ('' = todos). */
export function regimenesPara(regimenes, tipo) {
  return (regimenes || []).filter(function (r) {
    if (tipo === 'moral') { return !!r.moral; }
    if (tipo === 'fisica') { return !!r.fisica; }
    return !!(r.moral || r.fisica);
  });
}

/** Con la casilla marcada, los de la cuenta (si alguno vale); si no, el catálogo para el tipo de persona. */
export function regimenesOfrecidos(prep, propios, tipo) {
  var cat = regimenesPara((prep && prep.regimenes) || [], tipo);
  if (propios) {
    var cuenta = (prep && prep.regimenes_cuenta) || [];
    var deCuenta = cat.filter(function (r) { return cuenta.indexOf(r.clave) >= 0; });
    if (deCuenta.length) { return deCuenta; }
  }
  return cat;
}

/** Usos que admiten el régimen y el tipo de persona; nunca CN01 ni CP01. */
export function usosPara(usos, regimen, tipo) {
  return (usos || []).filter(function (u) {
    if (USOS_EXCLUIDOS.indexOf(u.clave) >= 0) { return false; }
    var persona = (tipo === 'moral') ? !!u.moral : ((tipo === 'fisica') ? !!u.fisica : !!(u.moral || u.fisica));
    return persona && (u.regimenes || []).indexOf(txt(regimen)) >= 0;
  });
}

/** G03 si se permite; si no, S01; si no, el primero. */
export function usoPorOmision(usos) {
  var c = (usos || []).map(function (u) { return u.clave; });
  if (c.indexOf('G03') >= 0) { return 'G03'; }
  if (c.indexOf('S01') >= 0) { return 'S01'; }
  return c[0] || '';
}

export function valoresIniciales(prep, propios) {
  var c = (prep && prep.cuenta) || {};
  var rfc = propios ? normalizarRfc(c.rfc) : '';
  var tipo = tipoPersona(rfc);
  var regs = propios ? regimenesOfrecidos(prep, true, tipo === 'generico' ? '' : tipo) : [];
  var regimen = regs.length ? regs[0].clave : '';
  var uso = regimen ? usoPorOmision(usosPara(prep.usos, regimen, tipo)) : '';
  return {
    propios: !!propios, rfc: rfc, nombre: propios ? txt(c.nombre) : '', cp: propios ? txt(c.cp) : '',
    regimen: regimen, uso: uso, correo: txt((prep && prep.correo_usuario) || c.correo), copia: false,
  };
}

/** El paso 1 antes de pasar al resumen: { ok } o { ok:false, campo, msg }. */
export function validarPaso1(d, prep) {
  var r = validarRfc(d.rfc);
  if (!r.ok) { return { ok: false, campo: 'rfc', msg: r.msg }; }
  if (!normalizarNombre(d.nombre)) {
    return { ok: false, campo: 'nombre', msg: 'Escribe la razón social como aparece en la Constancia de Situación Fiscal.' };
  }
  var c = validarCp(d.cp);
  if (!c.ok) { return { ok: false, campo: 'cp', msg: c.msg }; }
  var tipo = tipoPersona(r.rfc);
  var regs = regimenesOfrecidos(prep, !!d.propios, tipo).map(function (x) { return x.clave; });
  if (regs.indexOf(txt(d.regimen)) < 0) { return { ok: false, campo: 'regimen', msg: 'Elige un régimen fiscal válido para ese RFC.' }; }
  var usos = usosPara(prep.usos, d.regimen, tipo).map(function (x) { return x.clave; });
  if (usos.indexOf(txt(d.uso)) < 0) { return { ok: false, campo: 'uso', msg: 'Ese uso del CFDI no aplica al régimen fiscal elegido.' }; }
  if (correos(d.correo) === null) { return { ok: false, campo: 'correo', msg: 'Escribe uno o dos correos válidos, separados por coma.' }; }
  return { ok: true };
}

/** "601 · General de Ley Personas Morales" (o la clave sola si no está en la lista). */
export function textoDe(lista, clave) {
  var x = (lista || []).filter(function (e) { return e.clave === clave; })[0];
  return x ? x.clave + ' · ' + x.texto : txt(clave);
}

/** Las filas del resumen del paso 2: [etiqueta, valor]. */
export function armarResumen(d, prep, mx) {
  var dest = correos(d.correo) || [];
  // El correo de la cuenta TAL COMO lo usará el servidor (Orden_factura::correo_copia: el primero válido), no el
  // campo crudo, que puede traer varios.
  var cuenta = txt(prep && prep.correo_copia);
  if (d.copia && cuenta && dest.map(function (x) { return x.toLowerCase(); }).indexOf(cuenta.toLowerCase()) < 0) {
    dest = dest.concat([cuenta]);
  }
  var orden = (prep && prep.orden) || {};
  return [
    ['RFC', normalizarRfc(d.rfc)],
    ['Razón social', normalizarNombre(d.nombre)],
    ['Régimen fiscal', textoDe(prep.regimenes, d.regimen)],
    ['Código postal', txt(d.cp).trim()],
    ['Uso del CFDI', textoDe(prep.usos, d.uso)],
    ['Forma de pago', txt(orden.forma)],
    ['Total', (mx || totales.mx)(orden.total)],
    ['Se envía a', dest.join(', ')],
  ];
}

/** Lo que viaja a generar_ajax. */
export function datosGenerar(ordenId, d) {
  return {
    orden_id: ordenId, propios: d.propios ? '1' : '0', rfc: normalizarRfc(d.rfc), nombre: normalizarNombre(d.nombre),
    cp: txt(d.cp).trim(), regimen: txt(d.regimen), uso: txt(d.uso), correo: txt(d.correo).trim(),
    copia_cuenta: d.copia ? '1' : '0', confirmado: '1',
  };
}

/** Qué hacer con la respuesta de generar_ajax (§4.4). paso 1 = corregir; 2 = error; 3 = resultado. */
export function resultadoGenerar(r) {
  var code = r ? txt(r.code) : '';
  if (code === '200' && r.conciliada) {
    // Ya existía un CFDI de esta compra (un intento anterior): se ligó y se dice a nombre de quién está, que puede
    // no ser lo que se acaba de capturar (ruling T5-R2).
    var previa = [normalizarRfc(r.receptor_rfc), txt(r.receptor_nombre).trim()].filter(Boolean).join(' · ');
    return { paso: 3, tipo: 'exito', uuid: txt(r.uuid), pdf: txt(r.pdf), xml: txt(r.xml), correo: txt(r.correo), ya: true, previa: previa };
  }
  if (code === '200') {
    return { paso: 3, tipo: 'exito', uuid: txt(r.uuid), pdf: txt(r.pdf), xml: txt(r.xml), correo: txt(r.correo), ya: false };
  }
  if (code === '5020' || code === '4220') {
    return { paso: 1, tipo: 'campo', campo: txt(r.campo), msg: txt(r.msg), aviso: code === '5020' ? txt(r.aviso || MSG_NO_EMITIDA) : '' };
  }
  if (code === '5021') {
    return { paso: 3, tipo: 'duda', msg: txt(r.msg) || 'Estamos confirmando tu factura con el SAT; te avisamos por correo.' };
  }
  if (code === '4090' && r.pdf) {
    return { paso: 3, tipo: 'exito', uuid: '', pdf: txt(r.pdf), xml: txt(r.xml), correo: '', ya: true };
  }
  return { paso: 2, tipo: 'error', msg: (r && r.msg) ? txt(r.msg) : 'Tu factura aún no se genera. Revisa la lista en unos minutos antes de volver a pedirla.' };
}

function msgDe(j) { return j ? txt(j.msg || j.message).trim() : ''; }

/**
 * generar_ajax sin una respuesta JSON de éxito (ruling P9). 419 (sesión/token), 403 (permiso) y 429 (tope de
 * peticiones) los responde el servidor ANTES de tocar la orden: no se envió nada al SAT y se puede volver a
 * intentar (tras recargar; el 429, en un momento). Cualquier otra cosa —sin red, 5xx, un timeout, una respuesta
 * ilegible— puede haber llegado a timbrar: no se reintenta, se confirma.
 */
export function falloGenerar(status, json) {
  var s = Number(status) || 0;
  var nada = 'No se envió nada al SAT. No se emitió ninguna factura.';
  if (s === 419) {
    return { paso: 2, tipo: 'error', reintento: 'recargar',
      msg: 'Necesitamos confirmar tu sesión. ' + nada + ' Recarga la página y vuelve a intentarlo.' };
  }
  if (s === 403) {
    var m = msgDe(json) || 'Para facturar esta compra hace falta un permiso que da quien administra tu cuenta; pídele que te dé acceso desde Configuración › Usuarios y permisos.';
    return { paso: 2, tipo: 'error', reintento: 'recargar', msg: m + ' ' + nada };
  }
  if (s === 429) {
    return { paso: 2, tipo: 'error', reintento: 'ya',
      msg: 'Recibimos muchas solicitudes seguidas. ' + nada + ' Espera un momento y vuelve a intentarlo.' };
  }
  return { paso: 3, tipo: 'duda',
    msg: 'No recibimos la respuesta a tiempo y tu factura pudo haberse emitido: no la vuelvas a pedir, la estamos confirmando. Cierra esta ventana y revisa la lista en unos minutos; si se emitió, te llega por correo.' };
}

/** preparar_ajax sin respuesta de éxito: el mensaje del servidor (403, 419) o uno genérico. */
export function textoFalloPreparar(status, json) {
  return msgDe(json) || 'Aún no logramos preparar tu factura. Revisa tu conexión y vuelve a intentarlo.';
}

/** Lo que pinta la columna "Factura" de una fila (§3). */
export function accionDeFila(f) {
  if (!f) { return { tipo: 'texto', texto: '', clase: 'fd-fc-tenue' }; }
  if (f.estado === 'facturada') { return { tipo: 'descargas', pdf: txt(f.pdf), xml: txt(f.xml) }; }
  if (f.estado === 'facturable') {
    return f.puede ? { tipo: 'boton', limite: txt(f.limite) }
                   : { tipo: 'texto', texto: 'Facturarla requiere un permiso de quien administra tu cuenta', clase: 'fd-fc-tenue' };
  }
  var espera = (f.estado === 'en_proceso' || f.estado === 'en_duda');
  return { tipo: 'texto', texto: txt(f.texto), clase: espera ? 'fd-fc-espera' : 'fd-fc-tenue' };
}

/** Solo enlaces https o del mismo origen ('' si no). */
export function urlDescarga(url, origen) {
  var u = txt(url);
  if (/^https:\/\//i.test(u)) { return u; }
  if (origen && u.indexOf(origen + '/') === 0) { return u; }
  return '';
}

/** La respuesta de listar_ajax lista para pintar. */
export function vistaLista(r) {
  // `ocultable`: la tarjeta que nace oculta (cara de venta) solo se queda oculta si el servidor CONTESTÓ que no
  // hay compras; si la lista falló, se muestra con el error (no se esconde una compra que quizá sí existe).
  if (!r || txt(r.code) !== '200') {
    return { aviso: '', vacio: 'Aún no logramos traer tus compras. Recarga la página en un momento.', filas: [], nota: '', ocultable: false };
  }
  var filas = Array.isArray(r.filas) ? r.filas : [];
  return {
    aviso: r.disponible === false ? txt(r.aviso_no_disponible) : '',
    vacio: filas.length ? '' : 'Todavía no hay compras pagadas.',
    filas: filas,
    // El recorte de meses anteriores (ruling P13): visible, nunca en silencio.
    nota: txt(r.nota),
    ocultable: !filas.length,
  };
}

/**
 * La respuesta de v2/cuenta/suscripcion/recibos_ajax lista para pintar en "Compras y facturas" de Suscripción. Si Stripe falló,
 * el servidor manda igual los pagos únicos (el prepago, que no depende de Stripe) con un `aviso`: se pintan, y
 * nunca se dice "Todavía no hay comprobantes" cuando lo que pasó es que no se pudieron leer.
 */
export function vistaRecibos(r) {
  if (!r) {
    return { recibos: [], aviso: '', nota: '', vacio: 'Aún no logramos traer tus comprobantes. Recarga la página en un momento.' };
  }
  var recibos = Array.isArray(r.recibos) ? r.recibos : [];
  var ok = (txt(r.code) === '200');
  var vacio = '';
  if (!recibos.length) {
    vacio = ok ? 'Todavía no hay comprobantes. Aquí aparecerán tus recibos de Stripe y, cuando la generes, la factura CFDI de cada cobro.'
      : (txt(r.msg) || 'Aún no logramos traer tus comprobantes. Recarga la página en un momento.');
  }
  return { recibos: recibos, aviso: ok ? '' : txt(r.aviso), nota: txt(r.nota), vacio: vacio };
}

/* -------------------------------------------------------------------------
   El estado del modal (puro): cada apertura tiene su número; una respuesta de generar_ajax solo se pinta si es
   de la apertura vigente, y mientras está en vuelo el modal no se cierra (se cerraría a medio timbrar y la
   respuesta caería en el modal de otra compra). `enVuelo` guarda el número de la apertura que espera respuesta.
   `bloqueo`: 'recargar' (403/419: el motivo se repite en el paso 2), 'hecho' (ya hay resultado) o ''.
   ------------------------------------------------------------------------- */

/** Lo más que el modal espera a generar_ajax sin dejar cerrar; después muestra "la estamos confirmando". */
export var ESPERA_MAX_MS = 90000;

export function estadoInicial() { return { seq: 0, enVuelo: 0, bloqueo: '', msgBloqueo: '' }; }

export function nuevaApertura(s) {
  s.seq += 1;
  s.enVuelo = 0;
  s.bloqueo = '';
  s.msgBloqueo = '';
  return s.seq;
}

/** El número de esta apertura si se puede pedir la factura; 0 si ya hay una en vuelo o está bloqueada. */
export function tomarGenerar(s) {
  if (s.enVuelo || s.bloqueo === 'recargar' || s.bloqueo === 'hecho') { return 0; }
  s.enVuelo = s.seq;
  return s.seq;
}

export function esVigente(s, mio) { return mio > 0 && mio === s.seq; }

/** Llegó la respuesta de `mio`: libera el vuelo si era el suyo; true si todavía se debe pintar. */
export function soltarVuelo(s, mio) {
  if (s.enVuelo === mio) { s.enVuelo = 0; }
  return esVigente(s, mio);
}

/** Se cumplió ESPERA_MAX_MS sin respuesta de `mio`: deja cerrar. true si actuó (el vuelo seguía siendo ése). */
export function vencerVuelo(s, mio) {
  if (!mio || s.enVuelo !== mio) { return false; }
  s.enVuelo = 0;
  s.bloqueo = 'hecho';
  return true;
}

export function puedeCerrar(s) { return !s.enVuelo; }

export function bloquear(s, tipo, msg) {
  if (tipo === 'ya') { return; }
  s.bloqueo = txt(tipo);
  s.msgBloqueo = txt(msg);
}

/** Por qué "Generar factura" está apagado (se repinta al volver al paso 2). */
export function mensajeBloqueo(s) { return s.bloqueo === 'recargar' ? s.msgBloqueo : ''; }

/* =========================================================================
   Navegador
   ========================================================================= */

var est = { prep: null, orden: 0, cambios: false };
var ctl = estadoInicial();

function $m() { return document.getElementById('fd-fc-modal'); }
function $c(id) { return document.getElementById('fd-fc-' + id); }
function $d(sel) { var m = $m(); return m ? m.querySelector(sel) : null; }
function accion(nombre) { return $d('[data-fc-accion="' + nombre + '"]'); }

function opciones(select, lista, valor) {
  select.innerHTML = '';
  lista.forEach(function (x) {
    var o = document.createElement('option');
    o.value = x.clave;
    o.textContent = x.clave + ' · ' + x.texto;
    o.selected = (x.clave === valor);
    select.appendChild(o);
  });
}

function leer() {
  return {
    propios: $c('propios').checked, rfc: $c('rfc').value, nombre: $c('nombre').value, cp: $c('cp').value,
    regimen: $c('regimen').value, uso: $c('uso').value, correo: $c('correo').value, copia: $c('copia').checked,
  };
}

function limpiarMensajes() {
  var m = $m();
  m.querySelectorAll('[data-fc-msg]').forEach(function (p) { p.hidden = true; p.textContent = ''; });
  var e = $d('[data-fc-error]');
  e.hidden = true;
  e.textContent = '';
}

function marcarCampo(campo, msg) {
  var p = campo ? $d('[data-fc-msg="' + campo + '"]') : null;
  if (p) {
    p.textContent = msg;
    p.hidden = false;
    var i = $c(campo);
    if (i && typeof i.focus === 'function') { i.focus(); }
    return;
  }
  var e = $d('[data-fc-error]');
  e.textContent = msg;
  e.hidden = false;
}

function mostrarPaso(n) {
  $m().querySelectorAll('[data-fc-paso]').forEach(function (p) { p.hidden = (p.getAttribute('data-fc-paso') !== String(n)); });
  $d('[data-fc-aviso]').hidden = !(n === 1 || n === 2);
  accion('corregir').hidden = (n !== 2);
  accion('revisar').hidden = (n !== 1);
  accion('generar').hidden = (n !== 2);
  accion('cerrar').textContent = (n === 3) ? 'Cerrar' : 'Ahora no';
}

function tipoActual() {
  var t = tipoPersona($c('rfc').value);
  return t === 'generico' ? '' : t;
}

function pintarUsos() {
  var usos = usosPara(est.prep.usos, $c('regimen').value, tipoActual());
  var actual = $c('uso').value;
  opciones($c('uso'), usos, usos.some(function (u) { return u.clave === actual; }) ? actual : usoPorOmision(usos));
}

function pintarRegimenes(preferido) {
  var regs = regimenesOfrecidos(est.prep, $c('propios').checked, tipoActual());
  var actual = preferido || $c('regimen').value;
  opciones($c('regimen'), regs, regs.some(function (r) { return r.clave === actual; }) ? actual : (regs[0] ? regs[0].clave : ''));
  pintarUsos();
}

function aplicarPropios(propios) {
  var v = valoresIniciales(est.prep, propios);
  ['rfc', 'nombre', 'cp'].forEach(function (k) {
    var i = $c(k);
    i.value = v[k];
    i.readOnly = propios;
  });
  $d('[data-fc-config]').hidden = !propios;
  pintarRegimenes(v.regimen);
}

function pintarCopia() {
  var cuenta = txt(est.prep && est.prep.correo_copia);
  var box = $d('[data-fc-copia]');
  var mismos = (correos($c('correo').value) || []).map(function (x) { return x.toLowerCase(); });
  box.hidden = !cuenta || mismos.indexOf(cuenta.toLowerCase()) >= 0;
  $d('[data-fc-copia-texto]').textContent = 'Enviar copia a ' + cuenta;
  if (box.hidden) { $c('copia').checked = false; }
}

function pintarOrden(o) {
  var box = $d('[data-fc-orden]');
  box.innerHTML = '';
  [[o.concepto, 'fd-fc-orden-t'],
   [totales.mx(o.total) + ' · pagada el ' + o.fecha + ' · ' + o.forma, 'fd-fc-orden-s'],
   [o.limite ? 'Puedes facturarla hasta el ' + o.limite + '.' : '', 'fd-fc-orden-s']].forEach(function (p) {
    if (!p[0]) { return; }
    var e = document.createElement('p');
    e.className = p[1];
    e.textContent = p[0];
    box.appendChild(e);
  });
  box.hidden = false;
}

function pintarResumen() {
  var dl = $d('[data-fc-resumen]');
  dl.innerHTML = '';
  armarResumen(leer(), est.prep, totales.mx).forEach(function (par) {
    var dt = document.createElement('dt');
    dt.textContent = par[0];
    var dd = document.createElement('dd');
    dd.textContent = par[1];
    dl.appendChild(dt);
    dl.appendChild(dd);
  });
}

function descargas(cont, pdf, xml) {
  var origen = window.location.origin;
  [[pdf, 'PDF'], [xml, 'XML']].forEach(function (p) {
    var url = urlDescarga(p[0], origen);
    if (!url) { return; }
    var a = document.createElement('a');
    a.className = 'btn btn-sm btn-outline-secondary';
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    var i = document.createElement('i');
    i.className = 'ti ti-download';
    i.setAttribute('aria-hidden', 'true');
    a.appendChild(i);
    a.appendChild(document.createTextNode(' ' + p[1]));
    cont.appendChild(a);
  });
}

function pintarResultado(res) {
  var box = $d('[data-fc-resultado]');
  box.innerHTML = '';
  var exito = (res.tipo === 'exito');
  // Una factura que ya existía (conciliada) puede estar a nombre de otros datos: se dice en ámbar, no en verde.
  var previa = exito && res.previa;
  var nota = document.createElement('div');
  nota.className = 'fd-nota ' + (previa ? 'fd-nota-warn' : (exito ? 'fd-nota-ok' : 'fd-nota-pri'));
  var i = document.createElement('i');
  i.className = 'ti ' + (previa ? 'ti-info-circle' : (exito ? 'ti-circle-check' : 'ti-clock'));
  i.setAttribute('aria-hidden', 'true');
  var t = document.createElement('div');
  if (previa) {
    t.textContent = 'Ya existía una factura de esta compra a nombre de ' + res.previa + '. No se emitió otra.'
      + (res.correo ? ' Te la mandamos a ' + res.correo + '.' : '');
  } else if (exito) {
    t.textContent = res.ya ? 'Esta compra ya tiene su factura.'
      : 'Listo, tu factura quedó timbrada' + (res.uuid ? ' (UUID ' + res.uuid + ')' : '') + '.'
        + (res.correo ? ' Te la mandamos a ' + res.correo + '.' : '');
  } else {
    t.textContent = res.msg;
  }
  nota.appendChild(i);
  nota.appendChild(t);
  box.appendChild(nota);
  if (exito) {
    var acts = document.createElement('div');
    acts.className = 'fd-fc-descargas';
    descargas(acts, res.pdf, res.xml);
    box.appendChild(acts);
  }
}

function abrir(ordenId) {
  var m = $m();
  if (!m || !window.FD || !window.bootstrap) { return; }
  if (!puedeCerrar(ctl)) { return; } // hay una factura en vuelo en este modal: no se reemplaza
  var mio = nuevaApertura(ctl);
  est.prep = null;
  est.orden = ordenId;
  pintarVuelo(false);
  accion('generar').disabled = false;
  limpiarMensajes();
  m.querySelectorAll('[data-fc-paso]').forEach(function (p) { p.hidden = true; });
  $d('[data-fc-resultado]').innerHTML = '';
  $d('[data-fc-orden]').hidden = true;
  $d('[data-fc-aviso]').hidden = true;
  $d('[data-fc-cargando]').hidden = false;
  ['corregir', 'revisar', 'generar'].forEach(function (n) { accion(n).hidden = true; });
  window.bootstrap.Modal.getOrCreateInstance(m).show();
  window.FD.ajax({ method: 'POST', url: m.getAttribute('data-url-preparar'), data: { orden_id: ordenId } })
    .done(function (r) {
      if (!esVigente(ctl, mio)) { return; } // se cerró y se abrió otra compra mientras cargaba
      $d('[data-fc-cargando]').hidden = true;
      if (!r || txt(r.code) !== '200') {
        mostrarPaso(0);
        marcarCampo('', (r && r.msg) ? r.msg : 'Aún no logramos preparar tu factura. Inténtalo de nuevo en un momento.');
        est.cambios = true;
        return;
      }
      est.prep = r;
      pintarOrden(r.orden);
      $c('propios').checked = true;
      aplicarPropios(true);
      $c('correo').value = valoresIniciales(r, true).correo;
      $c('copia').checked = false;
      pintarCopia();
      mostrarPaso(1);
    })
    .fail(function (jq) {
      if (!esVigente(ctl, mio)) { return; }
      $d('[data-fc-cargando]').hidden = true;
      mostrarPaso(0);
      marcarCampo('', textoFalloPreparar(jq && jq.status, jq && jq.responseJSON));
    });
}

/** El motivo del bloqueo (403/419) en el paso 2, y "Generar factura" apagado mientras dure. */
function pintarBloqueo() {
  var msg = mensajeBloqueo(ctl);
  accion('generar').disabled = !!ctl.bloqueo || !!ctl.enVuelo;
  if (msg) { marcarCampo('', msg); }
}

/** Con una factura en vuelo no se cierra el modal ni se corrige: «Ahora no», la ✕ y Corregir se apagan. */
function pintarVuelo(on) {
  var m = $m();
  accion('generar').disabled = on || !!ctl.bloqueo;
  accion('cerrar').disabled = on;
  accion('corregir').disabled = on;
  var x = m ? m.querySelector('.btn-close') : null;
  if (x) { x.disabled = on; }
}

function revisar() {
  limpiarMensajes();
  var v = validarPaso1(leer(), est.prep);
  if (!v.ok) { marcarCampo(v.campo, v.msg); return; }
  pintarResumen();
  mostrarPaso(2);
  pintarBloqueo();
}

function generar() {
  if (!est.prep) { return; }
  var mio = tomarGenerar(ctl);
  if (!mio) { return; }
  pintarVuelo(true);
  // Tope de espera: sin respuesta en ESPERA_MAX_MS se deja cerrar con "la estamos confirmando" (no se reintenta).
  // Si la respuesta llega después y el modal sigue en esta compra, se pinta la de verdad.
  var tope = window.setTimeout(function () {
    if (!vencerVuelo(ctl, mio)) { return; }
    est.cambios = true;
    pintarVuelo(false);
    mostrarPaso(3);
    pintarResultado(falloGenerar(0, null));
  }, ESPERA_MAX_MS);
  window.FD.ajax({ method: 'POST', url: $m().getAttribute('data-url-generar'), data: datosGenerar(est.orden, leer()) })
    .done(function (r) {
      window.clearTimeout(tope);
      est.cambios = true;
      if (!soltarVuelo(ctl, mio)) { return; } // de una apertura anterior: solo cuenta para repintar la lista
      pintarVuelo(false);
      var res = resultadoGenerar(r);
      if (res.paso === 3) {
        bloquear(ctl, 'hecho', '');
        mostrarPaso(3);
        pintarResultado(res);
        return;
      }
      limpiarMensajes();
      if (res.paso === 1) {
        mostrarPaso(1);
        marcarCampo(res.campo, res.msg + (res.aviso ? ' ' + res.aviso : ''));
        return;
      }
      mostrarPaso(2);
      marcarCampo('', res.msg);
    })
    .fail(function (jq) {
      window.clearTimeout(tope);
      var res = falloGenerar(jq && jq.status, jq && jq.responseJSON);
      if (res.paso === 3) { est.cambios = true; }
      if (!soltarVuelo(ctl, mio)) { return; }
      pintarVuelo(false);
      if (res.paso === 3) {
        // Sin respuesta: no se sabe si el servidor la timbró. No se reintenta; al cerrar se repinta la lista con lo
        // que diga el servidor (y la conciliación la cierra).
        bloquear(ctl, 'hecho', '');
        mostrarPaso(3);
        pintarResultado(res);
        return;
      }
      // 403/419/429: el servidor la rechazó antes de tocar la orden; no se envió nada (ruling P9). Tras un 403 o un
      // 419 el botón queda apagado hasta recargar, y el motivo se repite si vuelve al paso 2; tras un 429, se
      // puede volver a intentar en un momento.
      bloquear(ctl, res.reintento, res.msg);
      limpiarMensajes();
      mostrarPaso(2);
      marcarCampo('', res.msg);
      pintarBloqueo();
    });
}

function enlazarModal() {
  var m = $m();
  if (!m || m.dataset.fdFcBound) { return; }
  m.dataset.fdFcBound = '1';
  accion('revisar').addEventListener('click', revisar);
  accion('corregir').addEventListener('click', function () { limpiarMensajes(); mostrarPaso(1); });
  accion('generar').addEventListener('click', generar);
  $c('propios').addEventListener('change', function () { limpiarMensajes(); aplicarPropios($c('propios').checked); });
  $c('rfc').addEventListener('input', function () {
    var i = $c('rfc');
    var v = normalizarRfc(i.value);
    if (v !== i.value) { i.value = v; }
  });
  $c('rfc').addEventListener('change', function () {
    limpiarMensajes();
    var r = validarRfc($c('rfc').value);
    if (!r.ok) { marcarCampo('rfc', r.msg); }
    pintarRegimenes('');
  });
  $c('cp').addEventListener('change', function () {
    var c = validarCp($c('cp').value);
    if (!c.ok) { marcarCampo('cp', c.msg); }
  });
  $c('regimen').addEventListener('change', pintarUsos);
  $c('correo').addEventListener('input', pintarCopia);
  // Con la factura en vuelo no se cierra (Esc, el fondo, la ✕ o «Ahora no»): la respuesta caería en otra compra.
  m.addEventListener('hide.bs.modal', function (ev) {
    if (!puedeCerrar(ctl)) { ev.preventDefault(); }
  });
  m.addEventListener('hidden.bs.modal', function () {
    if (est.cambios) {
      est.cambios = false;
      document.dispatchEvent(new CustomEvent('fd:factura-compra'));
    }
  });
}

/** La celda "Factura" de una fila (la usan la tarjeta y "Compras y facturas" de Suscripción). */
function celda(f) {
  var td = document.createElement('td');
  td.className = 'fd-fc-celda';
  var a = accionDeFila(f);
  if (a.tipo === 'boton') {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm btn-primary';
    b.setAttribute('data-fc-generar', txt(f.id));
    var i = document.createElement('i');
    i.className = 'ti ti-file-invoice';
    i.setAttribute('aria-hidden', 'true');
    b.appendChild(i);
    b.appendChild(document.createTextNode(' Generar factura'));
    td.appendChild(b);
    if (a.limite) {
      var s = document.createElement('span');
      s.className = 'fd-fc-limite';
      s.textContent = 'Hasta el ' + a.limite;
      td.appendChild(s);
    }
  } else if (a.tipo === 'descargas') {
    var cont = document.createElement('div');
    cont.className = 'fd-fc-descargas';
    descargas(cont, a.pdf, a.xml);
    td.appendChild(cont);
  } else {
    var sp = document.createElement('span');
    sp.className = 'fd-fc-texto ' + a.clase;
    sp.textContent = a.texto;
    td.appendChild(sp);
  }
  return td;
}

/** Delegación del clic en "Generar factura" dentro de un contenedor (una vez por contenedor). */
function enlazar(cont) {
  if (!cont || cont.dataset.fdFcClick) { return; }
  cont.dataset.fdFcClick = '1';
  cont.addEventListener('click', function (ev) {
    var b = ev.target && ev.target.closest ? ev.target.closest('[data-fc-generar]') : null;
    if (!b) { return; }
    ev.preventDefault();
    abrir(Number(b.getAttribute('data-fc-generar')));
  });
}

function td(texto, clase) {
  var e = document.createElement('td');
  if (clase) { e.className = clase; }
  e.textContent = txt(texto);
  return e;
}

function pintarLista(sec, r) {
  var tb = sec.querySelector('[data-fd-compras-filas]');
  var aviso = sec.querySelector('[data-fd-compras-aviso]');
  var nota = sec.querySelector('[data-fd-compras-nota]');
  var v = vistaLista(r);
  tb.innerHTML = '';
  aviso.textContent = v.aviso;
  aviso.hidden = !v.aviso;
  if (nota) {
    nota.textContent = v.nota;
    nota.hidden = !v.nota;
  }
  if (v.vacio) {
    var tr = document.createElement('tr');
    var c = td(v.vacio, 'fd-vacio');
    c.colSpan = 5;
    tr.appendChild(c);
    tb.appendChild(tr);
    // Solo una respuesta buena sin compras deja oculta la tarjeta de la cara de venta; un error se muestra.
    sec.hidden = (sec.getAttribute('data-ocultar-vacia') === '1' && v.ocultable);
    return;
  }
  sec.hidden = false;
  v.filas.forEach(function (f) {
    var fila = document.createElement('tr');
    // Las clases fd-fc-* las usa el acomodo de teléfono (_cuenta.scss): la fila se apila y "Generar factura"
    // queda a la vista sin desplazar la tabla.
    fila.appendChild(td(f.fecha, 'num fd-t-nowrap fd-fc-fecha'));
    fila.appendChild(td(f.concepto, 'fd-fc-concepto'));
    fila.appendChild(td(totales.mx(f.total), 'num fd-t-der fd-fc-total'));
    fila.appendChild(td(f.forma, 'fd-t-detalle fd-fc-forma'));
    fila.appendChild(celda(f));
    tb.appendChild(fila);
  });
}

function montarLista(sec) {
  if (sec.dataset.fdFcLista) { return; }
  sec.dataset.fdFcLista = '1';
  var cargar = function () {
    window.FD.ajax({ method: 'POST', url: sec.getAttribute('data-url-listar'), data: { tipo: sec.getAttribute('data-fd-compras') } })
      .done(function (r) { pintarLista(sec, r); })
      .fail(function () { pintarLista(sec, null); });
  };
  enlazar(sec);
  document.addEventListener('fd:factura-compra', cargar);
  cargar();
}

// Registro global y montaje: SOLO en el navegador (bajo Vitest no hay `window`).
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.FD = window.FD || {};
  window.FD.facturaCompra = { abrir: abrir, celda: celda, enlazar: enlazar, vistaRecibos: vistaRecibos };
  document.addEventListener('DOMContentLoaded', function () {
    if (!window.FD.ajax) { return; }
    enlazarModal();
    document.querySelectorAll('[data-fd-compras]').forEach(montarLista);
  });
}
